"use client";

import { setClientToken } from "@onsand/api";
import { auth } from "@onsand/auth";
import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { CircleAlert, Eye, EyeOff, Loader2, Lock, Mail } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { type FormEvent, useState } from "react";
import {
	AUTH_FIELD,
	AUTH_FIELD_ICON,
	AUTH_LABEL,
	AUTH_SUBMIT,
} from "@/components/auth/field-styles";
import { SocialButtons } from "@/components/auth/social-buttons";
import { Button } from "@/components/ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Label } from "@/components/ui/label";
import { Link, useRouter } from "@/i18n/navigation";
import {
	type SignupErrorKey,
	type SocialErrorKey,
	signupErrorKey,
} from "@/lib/camp/auth/auth-errors";
import { setMailLanguage } from "@/lib/camp/auth/mail-language";
import { cn } from "@/lib/utils";

const MIN_PASSWORD_LENGTH = 6;

export function SignupForm({ className }: { className?: string }) {
	const router = useRouter();
	const t = useTranslations("signup");
	const tl = useTranslations("login");
	const locale = useLocale();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState<
		{ key: SignupErrorKey; scope: "signup" } | { key: SocialErrorKey; scope: "login" } | null
	>(null);
	const [loading, setLoading] = useState(false);

	async function submit(event: FormEvent) {
		event.preventDefault();
		setLoading(true);
		setError(null);
		try {
			const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
			// The mail is the next step, not a condition of the account: if it cannot be sent now,
			// the confirm screen offers to send it again.
			setMailLanguage(auth, locale);
			await sendEmailVerification(credential.user).catch(() => undefined);
			setClientToken(await credential.user.getIdToken());
			router.push("/");
		} catch (failure) {
			setError({ key: signupErrorKey(failure), scope: "signup" });
			// Only a failure frees the button: on success the page is already on its way out.
			setLoading(false);
		}
	}

	return (
		<section
			aria-labelledby="signup-title"
			className={cn("rounded-2xl bg-card p-6 shadow-xs/5 sm:p-8", className)}
		>
			<div className="w-full">
				<h1
					className="text-balance font-heading font-semibold text-3xl leading-tight tracking-tight"
					id="signup-title"
				>
					{t("title")}
				</h1>
				<p className="mt-2 text-base text-muted-foreground">{t("description")}</p>

				<div className="mt-8">
					<SocialButtons
						onError={(key) => setError(key && { key, scope: "login" })}
						onSignedIn={() => router.push("/")}
					/>
				</div>
				<div className="my-6 flex items-center gap-3 text-muted-foreground text-sm">
					<span className="h-px flex-1 bg-border" />
					{tl("orWithEmail")}
					<span className="h-px flex-1 bg-border" />
				</div>

				<form className="grid gap-5" onSubmit={submit}>
					<div className="grid gap-2">
						<Label className={AUTH_LABEL} htmlFor="email">
							{tl("email")}
						</Label>
						<InputGroup className={AUTH_FIELD}>
							<InputGroupAddon>
								<Mail aria-hidden="true" className={AUTH_FIELD_ICON} />
							</InputGroupAddon>
							<InputGroupInput
								autoCapitalize="none"
								autoComplete="email"
								id="email"
								inputMode="email"
								name="email"
								onChange={(event) => setEmail(event.target.value)}
								placeholder={tl("emailPlaceholder")}
								required
								spellCheck={false}
								type="email"
								value={email}
							/>
						</InputGroup>
					</div>

					<div className="grid gap-2">
						<Label className={AUTH_LABEL} htmlFor="password">
							{tl("password")}
						</Label>
						<InputGroup className={AUTH_FIELD}>
							<InputGroupAddon>
								<Lock aria-hidden="true" className={AUTH_FIELD_ICON} />
							</InputGroupAddon>
							<InputGroupInput
								autoComplete="new-password"
								id="password"
								minLength={MIN_PASSWORD_LENGTH}
								name="password"
								onChange={(event) => setPassword(event.target.value)}
								required
								type={showPassword ? "text" : "password"}
								value={password}
							/>
							<InputGroupAddon align="inline-end">
								<Button
									aria-label={showPassword ? tl("hidePassword") : tl("showPassword")}
									className="size-11 text-muted-foreground hover:text-foreground sm:size-11"
									onClick={() => setShowPassword((visible) => !visible)}
									size="icon"
									type="button"
									variant="ghost"
								>
									{showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
								</Button>
							</InputGroupAddon>
						</InputGroup>
						<p className="text-muted-foreground text-sm">{t("passwordHint")}</p>
					</div>

					{error && (
						<p
							className="flex items-start gap-2 text-pretty text-destructive-foreground text-sm"
							role="alert"
						>
							<CircleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
							{error.scope === "signup" ? t(error.key) : tl(error.key)}
						</p>
					)}

					<Button className={AUTH_SUBMIT} disabled={loading} type="submit">
						{loading ? (
							<>
								<Loader2 aria-hidden="true" className="size-5 animate-spin" />
								{t("submitting")}
							</>
						) : (
							t("submit")
						)}
					</Button>
				</form>

				<p className="mt-6 text-center text-muted-foreground text-sm">
					{t("hasAccount")}{" "}
					<Link
						className="inline-flex min-h-11 items-center font-medium text-foreground underline underline-offset-4 hover:text-primary sm:min-h-8"
						href="/login"
					>
						{t("logIn")}
					</Link>
				</p>
			</div>
		</section>
	);
}
